/**
 * QaFollowersTab.tsx
 *
 * Followers and assignees for a single QA case (patients_qa_followers).
 * A follower is either an individual user or a user group (role). Assignees
 * are a subset of followers (isAssignee), mirroring the task assignee model:
 * all assignees follow, but not all followers are assignees.
 *
 * The same component drives two tabs via `mode`:
 *   - mode="assignees": lists/manages rows with isAssignee=1
 *   - mode="followers": lists/manages plain followers (isAssignee=0)
 *
 * When `caseId` is null (create flow), the component runs in draft mode: the
 * selected users/groups are buffered by the parent and flushed once the case
 * is created.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import * as qaSvc from '@/services/qa.service';
import * as adminSvc from '@/services/admin.service';
import { Trash2, User, Users } from 'lucide-react';

export interface QaFollowerDraftItem {
  followerType: qaSvc.QaFollowerType;
  followerId: number;
  name: string;
}

export interface QaFollowerDraft {
  items: QaFollowerDraftItem[];
  setItems: (items: QaFollowerDraftItem[]) => void;
}

interface QaFollowersTabProps {
  caseId: number | null;
  mode?: 'followers' | 'assignees';
  /** Required when `caseId` is null: buffers the selections for create. */
  draft?: QaFollowerDraft;
}

export function QaFollowersTab({ caseId, mode = 'followers', draft }: QaFollowersTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isAssigneeMode = mode === 'assignees';
  const noun = isAssigneeMode ? 'assignee' : 'follower';
  const isDraft = caseId === null;

  const [selected, setSelected] = useState<string | null>(null);

  const followersQuery = useQuery({
    queryKey: ['qa-followers', caseId],
    queryFn: () => qaSvc.listQaFollowers(caseId as number),
    enabled: !isDraft,
  });

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminSvc.listUsers(),
  });

  const rolesQuery = useQuery({
    queryKey: ['account-roles'],
    queryFn: () => adminSvc.listAccountRoles(),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['qa-followers', caseId] });
  }

  // Picker options: groups first, then individual users. Encoded as
  // "<type>:<id>" so a single Select can offer both kinds.
  const options = useMemo(() => {
    const groupOpts = (rolesQuery.data ?? []).map((r) => ({
      value: `group:${r.roleId}`,
      label: `Group — ${r.roleTitle}`,
    }));
    const userOpts = (usersQuery.data ?? [])
      .filter((u) => u.active)
      .map((u) => ({
        value: `user:${u.userId}`,
        label: `User — ${[u.firstName, u.lastName].filter(Boolean).join(' ').trim() || `#${u.userId}`}`,
      }));
    return [...groupOpts, ...userOpts];
  }, [rolesQuery.data, usersQuery.data]);

  function parseSelected(): { type: qaSvc.QaFollowerType; id: number; name: string } | null {
    if (!selected) return null;
    const [type, idStr] = selected.split(':');
    const opt = options.find((o) => o.value === selected);
    const name = (opt?.label ?? '').replace(/^(Group|User)\s+—\s+/, '');
    return { type: type as qaSvc.QaFollowerType, id: Number(idStr), name };
  }

  const addFollower = useMutation({
    mutationFn: () => {
      const parsed = parseSelected();
      if (!parsed) throw new Error('Select a user or group first.');
      return qaSvc.addQaFollower(caseId as number, {
        followerType: parsed.type,
        followerId: parsed.id,
        isAssignee: isAssigneeMode,
      });
    },
    onSuccess: () => {
      setSelected(null);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : `Failed to add ${noun}.`),
  });

  const removeFollower = useMutation({
    mutationFn: (followerId: number) => qaSvc.removeQaFollower(caseId as number, followerId),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e instanceof Error ? e.message : `Failed to remove ${noun}.`),
  });

  function handleAdd() {
    const parsed = parseSelected();
    if (!parsed) return;
    if (isDraft && draft) {
      if (draft.items.some((i) => i.followerType === parsed.type && i.followerId === parsed.id)) {
        setSelected(null);
        return;
      }
      draft.setItems([...draft.items, { followerType: parsed.type, followerId: parsed.id, name: parsed.name }]);
      setSelected(null);
      return;
    }
    addFollower.mutate();
  }

  function handleRemove(item: { draftIndex?: number; followerId?: number }) {
    if (isDraft && draft && item.draftIndex !== undefined) {
      draft.setItems(draft.items.filter((_, i) => i !== item.draftIndex));
      return;
    }
    if (item.followerId !== undefined) removeFollower.mutate(item.followerId);
  }

  // Unified row list across draft + saved modes.
  const rows = isDraft
    ? (draft?.items ?? []).map((it, idx) => ({
        key: `draft-${idx}`,
        followerType: it.followerType,
        name: it.name,
        draftIndex: idx,
        followerId: undefined as number | undefined,
      }))
    : (followersQuery.data ?? [])
        .filter((f) => (isAssigneeMode ? f.isAssignee : !f.isAssignee))
        .map((f) => ({
          key: `row-${f.patientsQaFollowerId}`,
          followerType: f.followerType,
          name: f.name,
          draftIndex: undefined as number | undefined,
          followerId: f.patientsQaFollowerId,
        }));

  return (
    <div className="space-y-5">
      {/* Add row */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            Add {noun}
          </label>
          <Select<string>
            value={selected}
            onChange={(v) => setSelected(v)}
            options={options}
            placeholder="Select user or group..."
            searchable
            clearable
            loading={usersQuery.isLoading || rolesQuery.isLoading}
          />
        </div>
        <Button
          onClick={handleAdd}
          disabled={!selected || addFollower.isPending}
          loading={addFollower.isPending}
          size="sm"
        >
          Add
        </Button>
      </div>

      {isAssigneeMode && (
        <p className="text-xs text-slate-500">Assignees are automatically followers of this case.</p>
      )}

      {/* List */}
      {!isDraft && followersQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No {noun}s yet.</p>
      ) : (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {rows.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                {f.followerType === 'group' ? (
                  <Users className="h-4 w-4 text-slate-400" />
                ) : (
                  <User className="h-4 w-4 text-slate-400" />
                )}
                <span>{f.name}</span>
                {f.followerType === 'group' && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">Group</span>
                )}
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-red-500"
                title={`Remove ${noun}`}
                onClick={() => handleRemove({ draftIndex: f.draftIndex, followerId: f.followerId })}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default QaFollowersTab;
